import CORE_RULES_CONFIG from '../rules/config.js'
import { toNumber } from '../../utils/numberUtils.js'

function normalizeCuit(value) {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length >= 10 ? digits : ''
}

function readCandidateTotals(input) {
  if (!input) return []
  if (Array.isArray(input)) {
    return input.map((item) => ({
      cuit: normalizeCuit(item?.cuit || item?.cuil || item?.idEmpleador || item?.identificador),
      total: toNumber(item?.total ?? item?.monto ?? item?.remuneracionTotal ?? item?.suma),
    }))
  }
  if (typeof input === 'object') {
    return Object.entries(input).map(([cuit, total]) => ({
      cuit: normalizeCuit(cuit),
      total: toNumber(total),
    }))
  }
  return []
}

export function extractResumenTotalesPorCuit(parserOutput = {}) {
  const confidence = Number(parserOutput?.resumenHistoriaLaboral?.sourceConfidence || 1)
  if (Number.isFinite(confidence) && confidence > 0 && confidence < CORE_RULES_CONFIG.resumenMinConfidence) {
    return new Map()
  }
  const candidates = [
    parserOutput?.resumenHistoriaLaboral?.totalesPorCuit,
    parserOutput?.resumenHistoriaLaboral?.totales_por_cuit,
    parserOutput?.resumenHistoriaLaboral?.empleadores,
    parserOutput?.resumenHistoriaLaboral,
    parserOutput?.totalesPorCuit,
    parserOutput?.totales_por_cuit,
    parserOutput?.debug?.totalesPorCuit,
    parserOutput?.debug?.totales_por_cuit,
    parserOutput?.etlSummary?.totalesPorCuit,
    parserOutput?.etlSummary?.totales_por_cuit,
  ]

  const map = new Map()
  candidates.forEach((candidate) => {
    readCandidateTotals(candidate).forEach((row) => {
      if (!row.cuit) return
      if (row.total <= 0) return
      if (!map.has(row.cuit)) map.set(row.cuit, row.total)
    })
  })
  return map
}

export function computeShadowBilling(rows = [], resumenTotalesPorCuit = new Map(), thresholdPct = CORE_RULES_CONFIG.desbalanceThresholdPct) {
  const threshold = Number.isFinite(Number(thresholdPct)) ? Number(thresholdPct) : CORE_RULES_CONFIG.desbalanceThresholdPct
  if (!(resumenTotalesPorCuit instanceof Map) || resumenTotalesPorCuit.size === 0) {
    return {
      enabled: false,
      thresholdPct: threshold,
      desbalancePorCuit: [],
      desbalanceGlobal: {
        totalResumen: 0,
        totalDetalle: 0,
        diferencia: 0,
        diferenciaPct: 0,
        hasDiscrepancy: false,
      },
      hasDiscrepancy: false,
    }
  }
  const detalleByCuit = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const cuit = normalizeCuit(row?.cuit)
    if (!cuit) return
    const monto = toNumber(row?.monto ?? row?.remImpSS ?? row?.montoOriginal)
    if (!(monto > 0)) return
    detalleByCuit.set(cuit, (detalleByCuit.get(cuit) || 0) + monto)
  })

  const allCuits = new Set([...Array.from(resumenTotalesPorCuit.keys()), ...Array.from(detalleByCuit.keys())])
  const desbalancePorCuit = []
  const resumenCuits = Array.from(resumenTotalesPorCuit.keys())
  const matchedResumenCuits = resumenCuits.filter((cuit) => Number(detalleByCuit.get(cuit) || 0) > 0)
  const matchedCuitsCount = matchedResumenCuits.length
  const coveragePct = resumenCuits.length > 0 ? (matchedCuitsCount / resumenCuits.length) * 100 : 0
  const minCoveragePct = Number(CORE_RULES_CONFIG.shadowBillingMinCoveragePct || 0)
  const minMatchedCuits = Number(CORE_RULES_CONFIG.shadowBillingMinMatchedCuits || 0)
  const hasCoverage = coveragePct >= minCoveragePct && matchedCuitsCount >= minMatchedCuits
  if (!hasCoverage) {
    return {
      enabled: false,
      thresholdPct: threshold,
      desbalancePorCuit: [],
      desbalanceGlobal: {
        totalResumen: 0,
        totalDetalle: 0,
        diferencia: 0,
        diferenciaPct: 0,
        hasDiscrepancy: false,
      },
      hasDiscrepancy: false,
      nonVerifiable: true,
      nonVerifiableReason: 'Cobertura monetaria insuficiente del resumen para conciliacion global',
      diagnostics: {
        resumenCuits: resumenCuits.length,
        matchedCuits: matchedCuitsCount,
        coveragePct: Number(coveragePct.toFixed(2)),
        minCoveragePct,
        minMatchedCuits,
      },
    }
  }
  allCuits.forEach((cuit) => {
    const totalResumen = toNumber(resumenTotalesPorCuit.get(cuit) || 0)
    const totalDetalle = toNumber(detalleByCuit.get(cuit) || 0)
    const diff = totalDetalle - totalResumen
    const base = Math.max(totalResumen, 1)
    const diffPct = (Math.abs(diff) / base) * 100
    const hasDiscrepancy = diffPct > threshold
    desbalancePorCuit.push({
      cuit,
      totalResumen,
      totalDetalle,
      diferencia: Number(diff.toFixed(2)),
      diferenciaPct: Number(diffPct.toFixed(4)),
      hasDiscrepancy,
      requiereRevisionUrgente: hasDiscrepancy,
    })
  })

  const totalResumenGlobal = desbalancePorCuit.reduce((acc, r) => acc + r.totalResumen, 0)
  const totalDetalleGlobal = desbalancePorCuit.reduce((acc, r) => acc + r.totalDetalle, 0)
  const globalDiff = totalDetalleGlobal - totalResumenGlobal
  const globalPct = (Math.abs(globalDiff) / Math.max(totalResumenGlobal, 1)) * 100

  return {
    enabled: resumenTotalesPorCuit.size > 0,
    thresholdPct: threshold,
    desbalancePorCuit,
    desbalanceGlobal: {
      totalResumen: Number(totalResumenGlobal.toFixed(2)),
      totalDetalle: Number(totalDetalleGlobal.toFixed(2)),
      diferencia: Number(globalDiff.toFixed(2)),
      diferenciaPct: Number(globalPct.toFixed(4)),
      hasDiscrepancy: globalPct > threshold,
    },
    hasDiscrepancy: desbalancePorCuit.some((r) => r.hasDiscrepancy) || globalPct > threshold,
    nonVerifiable: false,
    nonVerifiableReason: '',
    diagnostics: {
      resumenCuits: resumenCuits.length,
      matchedCuits: matchedCuitsCount,
      coveragePct: Number(coveragePct.toFixed(2)),
      minCoveragePct,
      minMatchedCuits,
    },
  }
}
